import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { LocalOffer as OfferIcon, Storefront as ShopIcon } from '@mui/icons-material';
import { formatOfferWindow } from '../../constants/community';
import { listingMeta } from '../../constants/directory';
import { clampedToLines } from '../../constants/layout';
import ModerateDialog from '../community/ModerateDialog';

/**
 * One offer, as a resident reads it.
 *
 * The dates come first after the title: an offer is only worth anything while
 * it runs, and "jusqu'à samedi" is what decides whether somebody goes.
 *
 * One announced for later is shown too — a shop announces the week-end on the
 * Monday — with a word saying so, because "Du 26 au 27 septembre" on its own
 * reads like something already running.
 *
 * Four bands, each the same height on every card of a row: when, what, the
 * details, and whose. An offer with a three-line title would otherwise put its
 * shop's name where its neighbour puts the offer itself, and the eye has to
 * start again on each card instead of running down the row. The title is cut
 * at two lines and the details at three — an offer that needs more than that
 * is a page, not a card, and its listing is one click away.
 */
function OfferCard({ offer, citySlug, isOwn = false, onModerated }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2.25, md: 2.75 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* When, on one line whatever the dates say: the band has to be the
          same height on every card, so the window is cut short rather than
          wrapped — it is repeated in full under the title of the listing. */}
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 1.25, minHeight: 24 }}>
        <OfferIcon fontSize="small" sx={{ color: 'secondary.main', flexShrink: 0 }} />
        <Chip
          size="small"
          variant="outlined"
          label={formatOfferWindow(offer.starts_on, offer.ends_on)}
          sx={{ minWidth: 0 }}
        />
        {offer.upcoming && <Chip size="small" color="info" label="À venir" sx={{ flexShrink: 0 }} />}
      </Stack>

      {/* What the offer is. The room for the details is kept even when there
          are none: a shop that wrote two words must not lift its card's whole
          contents above its neighbours'. */}
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" component="h3" title={offer.title} sx={clampedToLines(2, 1.2)}>
          {offer.title}
        </Typography>

        <Typography
          variant="body2"
          title={offer.description || undefined}
          sx={{ mt: 0.75, whiteSpace: 'pre-line', ...clampedToLines(3, 1.55) }}
        >
          {offer.description}
        </Typography>
      </Box>

      {/* Whose offer it is: the same line on every card of the row. */}
      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'surface.rule' }}>
        <Box
          component={RouterLink}
          to={`/prestataires/${offer.provider.slug}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'primary.main',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          <ShopIcon fontSize="small" />
          {offer.provider.name}
        </Box>

        {/* Always a second line, even for a shop with no neighbourhood on
            its listing: an absent one would lift the name above its
            neighbours' and undo the alignment. */}
        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
          {listingMeta(offer.provider.category, offer.provider.neighborhood)}
        </Typography>

        <ModerateDialog
          targetType="OFFER"
          targetUuid={offer.uuid}
          citySlug={citySlug}
          isOwn={isOwn}
          onModerated={onModerated}
        />
      </Box>
    </Paper>
  );
}

export default OfferCard;
